import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import {
  getExpiryStatus,
  getDaysUntilExpiry,
  getActiveExpiryReminders,
  getReminderDays,
  setReminderDays,
  dismissReminder,
  snoozeReminder,
  clearDismissedReminder,
  renewExpiryDate,
  DOCUMENT_CATEGORIES,
} from '../../services/docuvaultService';
import {
  clearRenewalTaskStatus,
  getRenewalTaskStatuses,
  setRenewalTaskStatus,
} from './renewalTaskStorage';

const {
  FiAlertTriangle, FiClock, FiCalendar, FiEye, FiRefreshCw,
  FiX, FiBell, FiChevronDown, FiChevronUp,
  FiSettings, FiCheck, FiSnooze,
} = FiIcons;

const SEVERITY_CONFIG = {
  expired:  { label: 'Expired',        icon: FiAlertTriangle, bg: 'bg-red-50',    border: 'border-red-200',    color: 'text-red-600',    chipBg: 'bg-red-100',  chipColor: 'text-red-700'  },
  critical: { label: 'Expiring ≤3d',  icon: FiAlertTriangle, bg: 'bg-red-50',    border: 'border-red-200',    color: 'text-red-500',    chipBg: 'bg-red-50',   chipColor: 'text-red-600'  },
  warning:  { label: 'Expiring ≤14d', icon: FiClock,         bg: 'bg-amber-50',  border: 'border-amber-200',  color: 'text-amber-600',  chipBg: 'bg-amber-100',chipColor: 'text-amber-700'},
  info:     { label: 'Expiring ≤30d', icon: FiClock,         bg: 'bg-blue-50',   border: 'border-blue-200',   color: 'text-blue-500',   chipBg: 'bg-blue-100',  chipColor: 'text-blue-700' },
};

const SNOOZE_OPTIONS = [
  { label: '1 day',   days: 1  },
  { label: '3 days',  days: 3  },
  { label: '7 days',  days: 7  },
  { label: '14 days', days: 14 },
  { label: '30 days', days: 30 },
];

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function RenewalTaskStatus({ complete, onToggle }) {
  return (
    <div className="mt-3 flex items-center justify-between gap-3 border-t border-brand-dark/20 pt-3">
      <div>
        <p className="text-[9px] font-black uppercase tracking-widest text-accent-navy">Renewal task</p>
        <p className={`text-[10px] font-medium ${complete ? 'text-green-700' : 'text-text-muted'}`}>
          {complete ? 'Marked complete' : 'Review this document and arrange renewal'}
        </p>
      </div>
      <button
        type="button"
        onClick={onToggle}
        className={`rounded-lg px-3 py-1.5 text-[9px] font-black uppercase tracking-widest transition-all ${
          complete
            ? 'border border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
            : 'bg-accent-navy text-white hover:bg-accent-navy/90'
        }`}
      >
        {complete ? 'Reopen task' : 'Mark complete'}
      </button>
    </div>
  );
}

// ── Single Reminder Row ──────────────────────────────────────────────────────
function ReminderRow({ doc, onView, onRenew, onDismiss, onSnooze, taskComplete, onToggleTask }) {
  const status   = getExpiryStatus(doc);
  const cfg      = SEVERITY_CONFIG[status] || SEVERITY_CONFIG.info;
  const days     = getDaysUntilExpiry(doc);
  const [showSnooze, setShowSnooze] = useState(false);
  const [showRenew, setShowRenew]   = useState(false);
  const [renewDate, setRenewDate]   = useState('');
  const [renewing, setRenewing]     = useState(false);
  const [renewErr, setRenewErr]     = useState('');

  const cat = DOCUMENT_CATEGORIES.find(c => c.id === doc.file_category) || DOCUMENT_CATEGORIES[DOCUMENT_CATEGORIES.length - 1];

  const daysLabel = days < 0
    ? `${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''} ago`
    : days === 0
      ? 'Today'
      : `In ${days} day${days !== 1 ? 's' : ''}`;

  const handleRenew = async () => {
    if (!renewDate) { setRenewErr('Please select a new expiry date.'); return; }
    setRenewing(true);
    setRenewErr('');
    try {
      await renewExpiryDate(doc.id, renewDate);
      clearDismissedReminder(doc.id);
      onRenew?.(doc.id, renewDate);
      setShowRenew(false);
    } catch (e) {
      setRenewErr(e.message || 'Failed to update expiry date.');
    } finally {
      setRenewing(false);
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 30, scale: 0.95 }}
      className={`rounded-2xl border ${cfg.border} ${cfg.bg} p-4 transition-all`}
    >
      <div className="flex items-start gap-3">
        {/* Severity icon */}
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.chipBg}`}>
          <SafeIcon icon={cfg.icon} className={`w-4 h-4 ${cfg.color}`} />
        </div>

        {/* Document info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <p className="text-sm font-bold text-accent-navy truncate">{doc.original_name}</p>
            <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${cfg.chipBg} ${cfg.chipColor}`}>
              {cfg.label}
            </span>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-text-muted flex-wrap">
            <span className="font-bold uppercase tracking-widest">{cat.label}</span>
            <span className="flex items-center gap-1">
              <SafeIcon icon={FiCalendar} className="w-3 h-3" />
              {formatDate(doc.expiry_date)}
            </span>
            <span className={`font-bold ${cfg.color}`}>{daysLabel}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={() => onView?.(doc)}
            className="p-2 text-text-muted hover:text-accent-navy hover:bg-white rounded-lg transition-all"
            title="View document"
          >
            <SafeIcon icon={FiEye} className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => { setShowRenew(!showRenew); setShowSnooze(false); }}
            className="p-2 text-text-muted hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"
            title="Renew / extend expiry"
          >
            <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => { setShowSnooze(!showSnooze); setShowRenew(false); }}
            className={`p-2 rounded-lg transition-all ${
              showSnooze ? 'text-accent-navy bg-white' : 'text-text-muted hover:text-accent-navy hover:bg-white'
            }`}
            title="Snooze reminder"
          >
            <SafeIcon icon={FiSnooze} className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onDismiss?.(doc.id)}
            className="p-2 text-text-muted hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
            title="Dismiss reminder"
          >
            <SafeIcon icon={FiX} className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <RenewalTaskStatus complete={taskComplete} onToggle={() => onToggleTask?.(doc.id)} />

      {/* Snooze panel */}
      <AnimatePresence>
        {showSnooze && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-brand-dark/30 flex-wrap">
              <span className="text-[9px] font-black uppercase tracking-widest text-text-muted">Snooze for:</span>
              {SNOOZE_OPTIONS.map(opt => (
                <button
                  key={opt.days}
                  onClick={() => { onSnooze?.(doc.id, opt.days); setShowSnooze(false); }}
                  className="px-3 py-1.5 bg-white border border-brand-dark rounded-lg text-[10px] font-bold text-accent-navy hover:border-accent-gold hover:bg-accent-gold/5 transition-all"
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Renew panel */}
      <AnimatePresence>
        {showRenew && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-3 pt-3 border-t border-brand-dark/30 space-y-2">
              <label className="text-[9px] font-black uppercase tracking-widest text-text-muted block">New Expiry Date</label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={renewDate}
                  onChange={e => setRenewDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="flex-1 px-3 py-2 bg-white border-2 border-brand-dark rounded-xl outline-none focus:border-accent-gold text-xs"
                />
                <button
                  onClick={handleRenew}
                  disabled={renewing}
                  className="flex items-center gap-1.5 px-4 py-2 bg-green-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-green-600 transition-all disabled:opacity-50"
                >
                  <SafeIcon icon={FiCheck} className="w-3.5 h-3.5" />
                  {renewing ? 'Saving…' : 'Extend'}
                </button>
              </div>
              {renewErr && <p className="text-[10px] text-red-600 font-bold">{renewErr}</p>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ── Preferences Toggle ────────────────────────────────────────────────────────
function ReminderPrefs({ onPrefChange }) {
  const [open, setOpen]       = useState(false);
  const [days, setDays]       = useState(getReminderDays());

  const handleSave = () => {
    setReminderDays(days);
    onPrefChange?.(days);
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-2 bg-white border border-brand-dark rounded-xl text-[10px] font-black uppercase tracking-widest text-text-muted hover:text-accent-navy hover:border-accent-navy transition-all"
      >
        <SafeIcon icon={FiSettings} className="w-3.5 h-3.5" />
        Reminder Settings
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="absolute right-0 top-full mt-2 z-20 bg-white rounded-2xl border-2 border-brand-dark shadow-xl p-4 w-64"
          >
            <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2">Remind me before expiry</p>
            <div className="flex items-center gap-2 mb-3">
              <input
                type="number"
                min={1}
                max={365}
                value={days}
                onChange={e => setDays(parseInt(e.target.value, 10) || 1)}
                className="flex-1 px-3 py-2 bg-brand-light border-2 border-brand-dark rounded-xl outline-none focus:border-accent-gold text-sm text-center font-bold"
              />
              <span className="text-xs font-bold text-text-muted">days</span>
            </div>
            <p className="text-[10px] text-text-muted font-light mb-3">
              Documents expiring within this window will appear as reminders. Expired documents always show.
            </p>
            <button
              onClick={handleSave}
              className="w-full py-2 bg-accent-navy text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all"
            >
              Save Preference
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────
const DocuVaultExpiryReminders = ({ docs, onView, onRenewed }) => {
  const [tick, setTick] = useState(0); // force re-render after dismiss/snooze
  const [collapsed, setCollapsed] = useState(false);
  const [taskStatuses, setTaskStatuses] = useState(getRenewalTaskStatuses);

  const reminders = useMemo(() => {
    void tick; // dependency
    return getActiveExpiryReminders(docs);
  }, [docs, tick]);

  // Group by severity
  const grouped = useMemo(() => {
    const groups = { expired: [], critical: [], warning: [], info: [] };
    reminders.forEach(doc => {
      const s = getExpiryStatus(doc);
      if (groups[s]) groups[s].push(doc);
    });
    // Sort each group by closest expiry first
    Object.keys(groups).forEach(k => {
      groups[k].sort((a, b) => {
        const da = a.expiry_date ? new Date(a.expiry_date).getTime() : Infinity;
        const db = b.expiry_date ? new Date(b.expiry_date).getTime() : Infinity;
        return da - db;
      });
    });
    return groups;
  }, [reminders]);

  const counts = {
    expired:  grouped.expired.length,
    critical: grouped.critical.length,
    warning:  grouped.warning.length,
    info:     grouped.info.length,
  };
  const totalCount = reminders.length;

  const handleDismiss = (docId) => {
    dismissReminder(docId);
    setTick(t => t + 1);
  };

  const handleSnooze = (docId, days) => {
    snoozeReminder(docId, days);
    setTick(t => t + 1);
  };

  const handleToggleTask = (docId) => {
    const nextComplete = !taskStatuses[String(docId)];
    setTaskStatuses(setRenewalTaskStatus(docId, nextComplete));
  };

  const handleRenewed = (docId, newDate) => {
    clearRenewalTaskStatus(docId);
    setTaskStatuses(getRenewalTaskStatuses());
    setTick(t => t + 1);
    onRenewed?.(docId, newDate);
  };

  if (totalCount === 0) return null;

  const hasExpired  = counts.expired > 0;
  const hasCritical = counts.critical > 0;
  const headerColor = hasExpired ? 'text-red-600' : hasCritical ? 'text-red-500' : 'text-amber-600';
  const headerBg    = hasExpired ? 'bg-red-50' : hasCritical ? 'bg-red-50' : 'bg-amber-50';
  const headerIcon = hasExpired || hasCritical ? FiAlertTriangle : FiBell;

  return (
    <div className={`rounded-[28px] border-2 ${hasExpired || hasCritical ? 'border-red-200' : 'border-amber-200'} overflow-hidden`}>
      {/* Header */}
      <div className={`${headerBg} px-5 py-4 flex items-center justify-between cursor-pointer`} onClick={() => setCollapsed(!collapsed)}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${hasExpired ? 'bg-red-100' : hasCritical ? 'bg-red-50' : 'bg-amber-100'}`}>
            <SafeIcon icon={headerIcon} className={`w-5 h-5 ${headerColor}`} />
          </div>
          <div>
            <h3 className={`text-sm font-black ${headerColor}`}>
              {totalCount} Document{totalCount !== 1 ? 's' : ''} Need Attention
            </h3>
            <p className="text-[10px] text-text-muted font-medium">
              {hasExpired && `${counts.expired} expired`}
              {hasExpired && (hasCritical || counts.warning > 0) && ' · '}
              {hasCritical && `${counts.critical} expiring ≤3 days`}
              {hasCritical && counts.warning > 0 && ' · '}
              {counts.warning > 0 && `${counts.warning} expiring ≤14 days`}
              {counts.info > 0 && ` · ${counts.info} expiring ≤30 days`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
          <ReminderPrefs onPrefChange={() => setTick(t => t + 1)} />
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-2 text-text-muted hover:text-accent-navy rounded-lg transition-all"
          >
            <SafeIcon icon={collapsed ? FiChevronDown : FiChevronUp} className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Renewal tasks are derived from the expiring documents and tracked in browser storage. */}
      <div className="border-t border-brand-dark/10 bg-white px-5 py-3">
        <p className="text-[9px] font-bold text-text-muted">
          Renewal task completion is saved in this browser only; it does not renew or update a document.
        </p>
      </div>

      {/* Reminder rows */}
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 space-y-3 bg-white">
              {['expired', 'critical', 'warning', 'info'].map(severity => {
                const group = grouped[severity];
                if (group.length === 0) return null;
                const cfg = SEVERITY_CONFIG[severity];
                return (
                  <div key={severity}>
                    <div className="flex items-center gap-2 mb-2 px-1">
                      <SafeIcon icon={cfg.icon} className={`w-3.5 h-3.5 ${cfg.color}`} />
                      <span className={`text-[9px] font-black uppercase tracking-widest ${cfg.color}`}>
                        {cfg.label} ({group.length})
                      </span>
                    </div>
                    <div className="space-y-2">
                      {group.map(doc => (
                        <ReminderRow
                          key={doc.id}
                          doc={doc}
                          onView={onView}
                          onRenew={handleRenewed}
                          onDismiss={handleDismiss}
                          onSnooze={handleSnooze}
                          taskComplete={Boolean(taskStatuses[String(doc.id)])}
                          onToggleTask={handleToggleTask}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DocuVaultExpiryReminders;